import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useWalletActions } from '../../context/WalletActionsProvider'
import {
  CHAPTER_BTN_CLASS,
  CHAPTER_EASE,
  DOT_BTN_CLASS,
  FRAME_EASE,
  I,
  MONO_CASE,
  SHOWCASE_STEPS,
  ShowcaseFrame,
} from './showcaseScenes'
import './LaunchShowcaseModal.css'

const COPY = {
  close: 'Close',
  next: 'Next',
  back: 'Back',
  start_trading: 'Start Trading',
  try_it: 'Try it',
  launch_showcase_intro_label: 'Intro',
  launch_showcase_intro_title: 'Lighter v3',
  launch_showcase_intro_body:
    "See what's new in V3. Use the arrow keys to explore, or jump to any update below.",
  launch_showcase_themes_label: 'Themes',
  launch_showcase_themes_title: 'Themes',
  launch_showcase_themes_body: 'Switch between dark and Lighter mode. Instantly and anytime.',
  launch_showcase_markets_label: 'Markets',
  launch_showcase_markets_title: 'Market selector',
  launch_showcase_markets_body:
    'A cleaner way to find and switch markets, with featured markets, asset classes, and favorites.',
  launch_showcase_accounts_label: 'Accounts',
  launch_showcase_accounts_title: 'Sub-accounts',
  launch_showcase_accounts_body:
    'A simpler way to see and manage your sub-accounts, with color coding to tell them apart easily.',
  launch_showcase_layout_label: 'Layout',
  launch_showcase_layout_title: 'Layout',
  launch_showcase_layout_body:
    'Customize your layout your way, or choose from Classic, Market Focused, and Swap views, with flexible panels and Market News.',
  launch_showcase_orders_label: 'Orders',
  launch_showcase_orders_title: 'Order types',
  launch_showcase_orders_body:
    'All the order types you know, now with Atomic Orders, randomized TWAPs, and more control over execution.',
}

function t(key) {
  return COPY[key] || key
}

function cx(base, cond = {}) {
  return [base, ...Object.keys(cond).filter((name) => cond[name])].join(' ')
}

function CloseGlyph() {
  return (
    <span data-icon="true" className="flex size-[14px] shrink-0 items-center justify-center">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" className="size-full">
        <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" />
      </svg>
    </span>
  )
}

const PRIMARY_BTN =
  'group inline-flex min-w-[92px] shrink-0 cursor-pointer items-center justify-center truncate rounded-pill bg-ink px-3 text-sm/sm font-medium text-bg shadow-border-button-primary transition-all hover:bg-ink/90 active:scale-[0.97] active:bg-ink/80 h-8 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-ink disabled:active:scale-100'
const MODAL_BTN =
  'group inline-flex shrink-0 cursor-pointer items-center justify-center truncate rounded-pill border-[0.5px] border-line bg-fill px-3 text-sm/sm font-medium text-ink shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp)] transition-all hover:bg-ink/6 active:scale-[0.97] h-8'

export default function LaunchShowcaseModal({ open, onClose }) {
  const { action, isWalletConnected, isSignWaiting, isSwitchingNetwork } =
    useWalletActions()
  const isActionDisabled = Boolean(isSignWaiting || isSwitchingNetwork)
  const [index, setIndex] = useState(0)
  const [fill, setFill] = useState(0)
  const isMobile = useMemo(
    () => typeof matchMedia !== 'undefined' && matchMedia('(max-width: 767px)').matches,
    [open],
  )

  const step = SHOWCASE_STEPS[index]
  const last = index === SHOWCASE_STEPS.length - 1
  const go = useCallback((next) => {
    setIndex(Math.min(Math.max(next, 0), SHOWCASE_STEPS.length - 1))
  }, [])

  const loopMs = step?.loopMs || 6000
  const indexRef = useRef(index)
  indexRef.current = index

  useEffect(() => {
    if (!open) setIndex(0)
  }, [open])

  useEffect(() => {
    if (!open || !step) return undefined
    setFill(0)
    const started = performance.now()
    let frame = 0
    const tick = (now) => {
      const t = Math.min((now - started) / loopMs, 1)
      setFill(t)
      if (t >= 1) {
        if (indexRef.current < SHOWCASE_STEPS.length - 1) {
          setIndex((current) => Math.min(current + 1, SHOWCASE_STEPS.length - 1))
        }
        return
      }
      frame = window.requestAnimationFrame(tick)
    }
    frame = window.requestAnimationFrame(tick)
    return () => window.cancelAnimationFrame(frame)
  }, [open, index, loopMs, step])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'ArrowRight') go(index + 1)
      if (event.key === 'ArrowLeft') go(index - 1)
      if (event.key === 'Escape' && !isActionDisabled) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, index, go, onClose, isActionDisabled])

  const finishShowcase = useCallback(() => {
    if (isActionDisabled) return
    if (isWalletConnected) {
      void action()
    }
  }, [action, isActionDisabled, isWalletConnected])

  const tryLayout = !isMobile && step.id === 'layout'
  const fillStyle = {
    transform: `scaleX(${fill})`,
    transformOrigin: 'left center',
  }

  if (!open || !step) return null

  return createPortal(
    <div className="launch-showcase-root" data-testid="launch-showcase-modal">
      <div className="launch-showcase-scrim" />
      <div
        role="dialog"
        aria-modal="true"
        className="launch-showcase-dialog flex flex-col overflow-hidden rounded-modal border-[0.5px] border-line bg-overlay text-ink shadow-elevate-modal"
      >
        <div
          className="relative w-full shrink-0 overflow-hidden"
          style={{ aspectRatio: `${I.w} / ${I.h}`, background: 'var(--showcase-sheet)' }}
        >
          <div
            key={step.id}
            aria-hidden
            className="absolute inset-0 select-none launch-showcase-frame-anim"
            style={{ animationTimingFunction: `cubic-bezier(${FRAME_EASE.join(',')})` }}
          >
            <ShowcaseFrame frame={step} paused={false} />
          </div>
          <div className="absolute top-3 right-3 z-40" style={{ mixBlendMode: 'difference' }}>
            <button
              type="button"
              aria-label={t('close')}
              data-testid="launch-showcase-close"
              className="flex size-8 items-center justify-center rounded-pill text-white opacity-75 transition-opacity hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-40"
              disabled={isActionDisabled}
              onClick={onClose}
            >
              <CloseGlyph />
            </button>
          </div>
        </div>

        <div
          className="flex min-h-[92px] shrink-0 flex-col justify-center gap-1.5 border-t-[0.5px] border-line px-5 py-4"
          aria-live="polite"
        >
          <div
            key={`${step.id}-copy`}
            className="launch-showcase-copy-anim"
            style={{ animationTimingFunction: `cubic-bezier(${CHAPTER_EASE.join(',')})` }}
          >
            <h3 className="text-lg font-medium text-ink">{t(step.titleKey)}</h3>
            <p className="mt-1 text-pretty text-sm leading-sm text-meta">{t(step.bodyKey)}</p>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-3 border-t-[0.5px] border-line p-3">
          {isMobile ? (
            <div className="flex items-center gap-1.5">
              {SHOWCASE_STEPS.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  aria-label={t(item.titleKey)}
                  aria-current={i === index ? 'step' : undefined}
                  onClick={() => go(i)}
                  className={cx(DOT_BTN_CLASS, {
                    'w-5 bg-ink/25': i === index,
                    'w-1.5 bg-ink/20 hover:bg-ink/45': i !== index,
                  })}
                >
                  {i === index ? (
                    <span
                      key={`fill-${index}`}
                      className="absolute inset-0 origin-left rounded-pill bg-ink"
                      style={fillStyle}
                    />
                  ) : null}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex min-w-0 items-center gap-1 overflow-hidden">
              {SHOWCASE_STEPS.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => go(i)}
                  aria-current={i === index ? 'step' : undefined}
                  data-testid={`launch-showcase-chapter-${item.id}`}
                  className={cx(CHAPTER_BTN_CLASS, {
                    'bg-ink/10 text-ink': i === index,
                    'text-dim hover:text-ink': i !== index,
                  })}
                  style={{ ...MONO_CASE, fontSize: 9.5, letterSpacing: '0.12em' }}
                >
                  {t(item.labelKey)}
                  <span
                    className="absolute inset-x-2 bottom-[3px] h-[1.5px] overflow-hidden rounded-pill bg-ink/15"
                    aria-hidden
                  >
                    {i < index ? <span className="absolute inset-0 bg-ink/60" /> : null}
                    {i === index ? (
                      <span
                        key={`fill-${index}`}
                        className="absolute inset-0 origin-left bg-ink/70"
                        style={fillStyle}
                      />
                    ) : null}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="flex shrink-0 items-center gap-2">
            {tryLayout ? (
              <button
                type="button"
                data-testid="launch-showcase-try"
                className={MODAL_BTN}
                onClick={() => {
                  onClose()
                  window.dispatchEvent(new CustomEvent('lighter:edit-layout'))
                }}
              >
                {t('try_it')}
              </button>
            ) : null}
            {index > 0 ? (
              <button
                type="button"
                data-testid="launch-showcase-back"
                className={MODAL_BTN}
                onClick={() => go(index - 1)}
              >
                {t('back')}
              </button>
            ) : null}
            <button
              type="button"
              data-testid="launch-showcase-next"
              className={PRIMARY_BTN}
              disabled={isActionDisabled}
              onClick={last ? finishShowcase : () => go(index + 1)}
            >
              {t(last ? 'start_trading' : 'next')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
